import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Heart, Eye, Users, Sprout, Zap, Compass, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";

import RichContent from "@/components/RichContent";
const ICONS = { Gratitude: Heart, Awareness: Eye, Unity: Users, Growth: Sprout, Energy: Zap };
const RETREAT = "https://images.unsplash.com/photo-1566815974688-89f997e0fccf?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function GaugePage() {
  const { data: gauge = [] } = useQuery({ queryKey: ["gauge"], queryFn: () => api.get("/gauge").then((r) => r.data) });

  return (
    <div data-testid="gauge-page">
      <Seo
        title="GAUGE — Gratitude, Awareness, Unity, Growth, Energy"
        description="GAUGE is Enhancing Your World's living compass — Gratitude, Awareness, Unity, Growth, Energy. A framework for conscious participation, self-discovery, and meaningful life design."
        path="/gauge"
      />
      <section className="relative py-28">
        <img src={RETREAT} alt="Retreat" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#144F75]/80" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <Compass className="mx-auto text-[#D98E73] mb-5" size={40} />
          <p className="eyw-label !text-[#D98E73] mb-3">The Methodology</p>
          <h1 className="font-serif text-5xl lg:text-6xl text-white leading-tight">GAUGE Your Life</h1>
          <p className="text-white/85 text-lg mt-5 max-w-2xl mx-auto">
            A living compass that helps participants reflect on conscious participation,
            relationship, stewardship, and the way daily actions shape the world.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
            <Link to="/gauge/app" data-testid="gauge-begin-cta" className="eyw-btn !bg-[#D98E73] hover:!bg-[#c67a60]">
              Begin Your Exploration <ArrowRight size={17} />
            </Link>
            <Link to="/coach" data-testid="gauge-coach-cta" className="eyw-btn-outline !border-white/60 !text-white hover:!bg-white/10">
              Daily Coach
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 lg:px-10 py-24 space-y-6">
        {gauge.map((g, i) => {
          const Icon = ICONS[g.pillar] || Compass;
          const alt = i % 2 === 1;
          return (
            <div key={g.id} data-testid={`gauge-pillar-${g.pillar.toLowerCase()}`} className={`eyw-card p-8 lg:p-10 grid lg:grid-cols-[auto_1fr] gap-8 items-start ${alt ? "lg:bg-[#F9F7F3]" : ""}`}>
              <div className="flex lg:flex-col items-center gap-4">
                <span className="grid place-items-center h-20 w-20 rounded-2xl bg-[#144F75] text-white font-serif text-4xl">{g.letter}</span>
                <span className="grid place-items-center h-12 w-12 rounded-full bg-[#8FA89B]/20 text-[#144F75]"><Icon size={22} /></span>
              </div>
              <div>
                <p className="eyw-label mb-1">{g.tagline}</p>
                <h2 className="font-serif text-3xl lg:text-4xl text-[#1A242B]">{g.pillar}</h2>
                <RichContent text={g.description} className="text-[#4A5D6B] mt-3 leading-relaxed" />
                {g.reflection_prompt && (
                  <div className="mt-5 rounded-xl border-l-4 border-[#D98E73] bg-[#D98E73]/[0.07] px-5 py-4">
                    <p className="eyw-label !text-[#D98E73] mb-1">Reflection</p>
                    <p className="font-serif text-xl italic text-[#144F75]">{g.reflection_prompt}</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </section>
    </div>
  );
}
